import type { ModuleIconComponent } from './moduleIconTypes'

export const CUSTOM_MODULE_ICON_NAMES = [
  'whatsapp',
  'app',
  'app-mobile',
  'app-desktop',
  'app-grid',
  'user-circle',
  'user-group',
  'user-square',
  'server-stack',
  'server-cloud',
  'server-database',
  'dashboard',
  'dashboard-grid',
  'dashboard-chart',
  'dashboard-analytics',
  'settings',
  'settings-gear',
  'settings-sliders',
  'settings-adjustments',
  'settings-cog',
  'notification',
  'notification-bell',
  'notification-bell-alert',
  'notification-bell-ring',
  'notification-badge',
  'notification-message',
] as const

export type ModuleIconEntry = {
  name: string
  component: ModuleIconComponent
}

export type ModuleIconCategoryGroup = {
  id: string
  label: string
  icons: ModuleIconEntry[]
}

const CUSTOM_ICON_SET = new Set<string>(CUSTOM_MODULE_ICON_NAMES)

type CategoryRule = {
  id: string
  label: string
  match: (name: string) => boolean
}

const CATEGORY_RULES: CategoryRule[] = [
  {
    id: 'custom',
    label: 'Personalizados',
    match: (name) => CUSTOM_ICON_SET.has(name),
  },
  {
    id: 'users',
    label: 'Usuários e pessoas',
    match: (name) =>
      /^(user|users|identification|hand-|face-|contact|person|baby|accessibility)/.test(name),
  },
  {
    id: 'communication',
    label: 'Comunicação',
    match: (name) =>
      /^(chat-bubble|envelope|phone|megaphone|rss|share|paper-airplane|at-symbol|message|mail|send|reply|inbox|voicemail|podcast|radio-tower)/.test(name),
  },
  {
    id: 'documents',
    label: 'Documentos e arquivos',
    match: (name) =>
      /^(document|folder|clipboard|archive|paper-clip|printer|inbox|file|notebook|book|sticky-note|scroll|library)/.test(name),
  },
  {
    id: 'charts',
    label: 'Gráficos e relatórios',
    match: (name) =>
      /^(chart-|presentation-chart-|trending|activity|gauge|kanban|gantt)/.test(name),
  },
  {
    id: 'business',
    label: 'Negócios e finanças',
    match: (name) =>
      /^(briefcase|building|banknotes|currency-|credit-card|receipt-|calculator|scale|percent-badge|wallet|shopping-|ticket|gift|store|landmark|factory|warehouse|badge-dollar|hand-coins|piggy-bank|invoice)/.test(name),
  },
  {
    id: 'technology',
    label: 'Tecnologia e dispositivos',
    match: (name) =>
      /^(server|cpu-chip|command-line|code-|computer-desktop|device-|wifi|signal|cloud|qr-code|battery-|circle-stack|bug-ant|bot|brain|circuit|workflow|network|database|terminal|monitor|laptop|smartphone|tablet|bluetooth|usb|hard-drive|component|layers|package|boxes|webhook|binary|blocks)/.test(name),
  },
  {
    id: 'media',
    label: 'Mídia e áudio',
    match: (name) =>
      /^(film|photo|video-|camera|musical-note|tv|play|pause|stop|speaker-|microphone|gif|backward|forward|volume|music|disc|clapperboard|headphones|image)/.test(name),
  },
  {
    id: 'maps',
    label: 'Mapas e localização',
    match: (name) =>
      /^(map|globe-|truck|navigation|compass|route|locate|milestone|signpost)/.test(name),
  },
  {
    id: 'security',
    label: 'Segurança e acesso',
    match: (name) =>
      /^(shield-|lock-|key|finger-print|scan-face|scan-eye|shield|siren)/.test(name),
  },
  {
    id: 'status',
    label: 'Status e alertas',
    match: (name) =>
      /^(bell|check|exclamation-|information-circle|question-mark-circle|x-circle|x-mark|minus-circle|fire|bolt|lifebuoy|no-symbol|alert|badge-check|badge-x|badge-alert|circle-alert|circle-check|circle-x|triangle-alert|octagon)/.test(name),
  },
  {
    id: 'time',
    label: 'Tempo e calendário',
    match: (name) =>
      /^(calendar|clock|sun|moon|timer|alarm|history|hourglass)/.test(name),
  },
  {
    id: 'actions',
    label: 'Ações e setas',
    match: (name) =>
      /^(arrow|arrows-|chevron|corner|move|redo|undo|refresh|repeat|rotate|expand|shrink|maximize|minimize|download|upload)/.test(name),
  },
  {
    id: 'interface',
    label: 'Interface e navegação',
    match: (name) =>
      /^(home|bars|squares|square-|view-|window|ellipsis|queue-list|list-bullet|numbered-list|funnel|adjustments|cog|cursor-arrow|magnifying-glass|eye|plus|minus|power|link|variable|rectangle-|table-cells|swatch|tag|hashtag|trash|layout|panel|sidebar|menu|grid|columns|rows|toggle|switch|slider|settings|search|filter|options|grip|mouse-pointer|pointer|crosshair|scan|zoom)/.test(name),
  },
  {
    id: 'education',
    label: 'Educação, ciência e ferramentas',
    match: (name) =>
      /^(academic-cap|beaker|light-bulb|book-open|bookmark|wrench|scissors|paint-brush|flag|heart|star|sparkles|trophy|cake|language|puzzle-piece|rocket-launch|newspaper|graduation|microscope|flask|atom|dna|stethoscope|syringe|pill|hospital|ambulance)/.test(name),
  },
  {
    id: 'lucide',
    label: 'Lucide',
    match: (name) => name.startsWith('lucide-'),
  },
]

const OTHER_CATEGORY: CategoryRule = {
  id: 'other',
  label: 'Outros',
  match: () => true,
}

export const getModuleIconCategory = (name: string): Pick<CategoryRule, 'id' | 'label'> => {
  const baseName = name.startsWith('lucide-') ? name.slice('lucide-'.length) : name
  const rule = CATEGORY_RULES.find((category) => category.id !== 'lucide' && category.match(baseName))
  if (rule) return rule
  if (name.startsWith('lucide-')) return { id: 'lucide', label: 'Lucide' }
  return OTHER_CATEGORY
}

export const groupModuleIconsByCategory = (
  icons: ModuleIconEntry[]
): ModuleIconCategoryGroup[] => {
  const buckets = new Map<string, ModuleIconCategoryGroup>()

  for (const icon of icons) {
    const { id, label } = getModuleIconCategory(icon.name)
    const bucket = buckets.get(id) ?? { id, label, icons: [] }
    bucket.icons.push(icon)
    buckets.set(id, bucket)
  }

  const orderedIds = [...CATEGORY_RULES.map((rule) => rule.id), OTHER_CATEGORY.id]

  return orderedIds
    .map((id) => buckets.get(id))
    .filter((group): group is ModuleIconCategoryGroup => !!group && group.icons.length > 0)
    .map((group) => ({
      ...group,
      icons: [...group.icons].sort((a, b) => a.name.localeCompare(b.name)),
    }))
}
